import type { Dispatch, ReactNode, SetStateAction } from 'react';
import { createContext, useContext, useMemo, useState } from 'react';

interface CsvDownloadValues {
  downloadFileId: string | undefined;
  selectedRange: string | undefined;
  fileName: string | undefined;
  groupName: string | undefined;
  dateColumnKey: string | null | undefined;
}

interface CsvDownloadSetters {
  setDownloadFileId: Dispatch<SetStateAction<string | undefined>>;
  setSelectedRange: Dispatch<SetStateAction<string | undefined>>;
  setFileName: Dispatch<SetStateAction<string | undefined>>;
  setGroupName: Dispatch<SetStateAction<string | undefined>>;
  setDateColumnKey: Dispatch<SetStateAction<string | null | undefined>>;
}

// Values context — re-renders readers (CsvWidgetExportButton, useAppCsvExport) when state changes
const CsvDownloadValuesContext = createContext<CsvDownloadValues | undefined>(undefined);

// Setters context — stable useState dispatchers, never triggers re-renders in writers
const CsvDownloadSettersContext = createContext<CsvDownloadSetters | undefined>(undefined);

export const CsvDownloadProvider = ({ children }: { children: ReactNode }) => {
  const [downloadFileId, setDownloadFileId] = useState<string>();
  const [selectedRange, setSelectedRange] = useState<string>();
  const [fileName, setFileName] = useState<string>();
  const [groupName, setGroupName] = useState<string>();
  const [dateColumnKey, setDateColumnKey] = useState<string | null | undefined>();

  const values = useMemo(
    () => ({ downloadFileId, selectedRange, fileName, groupName, dateColumnKey }),
    [downloadFileId, selectedRange, fileName, groupName, dateColumnKey]
  );

  // useState setters are guaranteed stable — this object never changes

  const setters = useMemo<CsvDownloadSetters>(
    () => ({ setDownloadFileId, setSelectedRange, setFileName, setGroupName, setDateColumnKey }),
    []
  );

  return (
    <CsvDownloadSettersContext value={setters}>
      <CsvDownloadValuesContext value={values}>{children}</CsvDownloadValuesContext>
    </CsvDownloadSettersContext>
  );
};

/** Read context values — use in components that display CSV download state. */
export const useCsvDownloadContext = () => {
  const context = useContext(CsvDownloadValuesContext);
  if (!context) throw new Error('useCsvDownloadContext must be used within CsvDownloadProvider');
  return context;
};

/** Write to context — use in components that update CSV download state. Subscribes only to
 *  stable setters so callers never re-render due to state changes in this context. */
export const useCsvDownloadSetters = () => {
  const context = useContext(CsvDownloadSettersContext);
  if (!context) throw new Error('useCsvDownloadSetters must be used within CsvDownloadProvider');
  return context;
};
